import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BankAccount, BookingOption, CyclingEvent, PROMOTION_BASE_TIER, PROMOTION_DEFAULT_LABELS,
  PROMOTION_TIER_SALES, PROMOTION_UPSELL_TIER, PromotionTier, TaxTreatment, activePromotion,
  formatEuro, payableCents, payableTaxLabel, promotionCoversEvent, promotionOffer, taxTreatmentOf,
  vatNote,
} from '@radevents/shared';
import bookingService from '../services/bookingService';
import BillingFields, {
  BILLING_CSS, BillingValue, billingComplete as isBillingComplete, billingRequest,
} from './organizer/BillingFields';
import BookingDone, { BOOKING_DONE_CSS } from './organizer/BookingDone';
import EventCardHero from './EventCardHero';
import EventCardBody from './EventCardBody';
import { promoCardClass } from '../utils/eventHero';
import { trackOrganizerFunnel } from '../services/trackingService';

interface Props {
  show: boolean;
  organizerId: number;
  /** Das Event, um das es geht — eine Hervorhebung hängt immer an einem. */
  eventId?: number;
  eventTitle?: string;
  /**
   * Dasselbe Event als ganzer Datensatz. Damit zeigt der Dialog **die echte Card dieses
   * Events** statt einer Skizze — wer eine Gestaltung kauft, soll sie an seinem eigenen Termin
   * sehen, nicht an einem Platzhalter. Fehlt es, entfällt nur die Vorschau.
   */
  event?: CyclingEvent;
  /** Vorschlag für Rechnungsempfänger und -anschrift aus den Stammdaten (nur Vorbelegung). */
  billingDefault: BillingValue;
  onClose: () => void;
  onBooked: () => void;
}

const FONT_MONO = "'Space Mono',monospace";
const INK = '#2C2C2C';
const MUTED = '#9A9494';
const BORDER = '#E5E1E1';
const BERRY = '#922C57';

const CSS = `
.bk-backdrop { position:fixed; inset:0; z-index:60; background:rgba(44,44,44,.45);
  display:flex; align-items:center; justify-content:center; padding:16px; }
.bk-sheet { width:100%; max-width:620px; max-height:90vh; overflow-y:auto; background:#fff;
  border-radius:20px; padding:26px 26px 20px; }
/* Die Bestaetigung bringt ihr eigenes Polster mit (.bd) — beides zusammen waere doppelt. */
.bk-sheet--done { padding:0; max-width:540px; }
.bk-title { font-family:'Archivo',sans-serif; font-weight:800; font-size:22px; letter-spacing:-.01em;
  color:${INK}; margin:0 0 6px; }
.bk-lead { font-size:14px; color:${MUTED}; margin:0 0 22px; line-height:1.5; }
.bk-legend { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.12em; text-transform:uppercase;
  color:${MUTED}; margin:0 0 10px; }

/* Rasterfarbe wie in der Übersicht: auf Weiß wäre der helle Rahmen nicht zu beurteilen, weil er
   dort gegen dieselbe Farbe steht wie der Cardgrund. */
.bk-stage { background:#EDEAE7; border-radius:14px; padding:14px; display:flex; justify-content:center; }
.bk-benefit { font-size:13px; color:${MUTED}; line-height:1.5; margin:9px 0 0; }
/* Warum gerade nichts zu buchen ist — an der Stelle, an der sonst der Preis steht. */
.bk-blocked { font-size:13.5px; line-height:1.55; color:#7A5E12; background:rgba(240,195,106,.16);
  border-radius:10px; padding:12px 13px; margin:0; }

/* ── Der Preis und das Häkchen ──────────────────────────────────────────────────────────────
   Ein Preis, ein Häkchen. Der Rahmen ist die Leistung, die dunkle Card ist die Differenz dazu:
   der Käufer bewertet nicht zwei Angebote, er entscheidet über einen Aufpreis. Die Vorschau
   darüber wechselt mit dem Häkchen — dieser Wechsel ist die Auskunft. */
.bk-price { display:flex; align-items:baseline; justify-content:space-between; gap:12px;
  flex-wrap:wrap; border:1px solid ${BORDER}; border-radius:14px; padding:14px 16px; margin-top:14px; }
.bk-price-name { font-size:15px; font-weight:700; color:${INK}; }
.bk-price-sub { display:block; font-size:13px; color:${MUTED}; margin-top:2px; }
.bk-price-amount { font-family:${FONT_MONO}; font-size:15px; color:${INK}; white-space:nowrap; }
.bk-price-amount span { color:${MUTED}; font-size:12px; }
/* Listenpreis vor dem Rabatt — grau und durchgestrichen, damit der gueltige Preis der auffaellige
   bleibt. Die Marke dahinter nennt den Grund; ohne sie liest sich ein durchgestrichener Preis wie
   eine Rabattaktion und nicht wie eine Leistung, die der Kunde bereits bezahlt hat. */
.bk-price-list { color:${MUTED}; margin-right:7px; text-decoration:line-through; }
.bk-discount { display:inline-block; margin-left:8px; font-family:${FONT_MONO}; font-size:9.5px;
  letter-spacing:.08em; text-transform:uppercase; color:${BERRY}; background:rgba(146,44,87,.09);
  border-radius:6px; padding:3px 7px; }
.bk-upsell { display:flex; gap:12px; align-items:flex-start; border:1px solid ${BORDER};
  border-radius:14px; padding:13px 16px; margin-top:10px; cursor:pointer;
  transition:border-color .15s, background .15s; }
.bk-upsell:hover { border-color:#C9C2C2; background:#FBFAFA; }
.bk-upsell--on { border-color:${BERRY}; background:rgba(146,44,87,.05);
  box-shadow:inset 0 0 0 1px ${BERRY}; }
.bk-upsell input { margin:3px 0 0; accent-color:${BERRY}; width:17px; height:17px; flex-shrink:0;
  cursor:pointer; }
.bk-upsell-body { flex:1; min-width:0; }
.bk-upsell-name { display:flex; justify-content:space-between; gap:12px; font-size:14.5px;
  font-weight:700; color:${INK}; }
.bk-upsell-extra { font-family:${FONT_MONO}; font-size:13px; color:${BERRY}; white-space:nowrap; }
.bk-upsell-text { display:block; font-size:13px; color:${MUTED}; margin-top:3px; line-height:1.45; }

/* Die Vorschau ist die echte Card, nur kleiner gerechnet: verfügbare Breite geteilt durch die
   Breite, in der die Card später steht — nie größer als 1:1. Die Höhe kommt gemessen zurück,
   weil sie vom Inhalt abhängt. */
.bk-preview { position:relative; overflow:hidden; }
.bk-preview-inner { position:absolute; top:0; left:0; transform-origin:top left;
  container:lp-cards / inline-size;
  /* Nur Anschauung: Klicks gehören dem Häkchen darunter. */
  pointer-events:none; }
.bk-preview-inner .event-card { cursor:inherit; }

.bk-field { display:block; margin:18px 0 0; }
.bk-field > span { display:block; font-size:14px; font-weight:700; color:${INK}; margin-bottom:6px; }
.bk-input { width:100%; box-sizing:border-box; background:#F5F3F3; border:1px solid ${BORDER};
  border-radius:10px; padding:11px 12px; font-family:inherit; font-size:14px; color:${INK}; outline:none; }
.bk-input:focus { border-color:${BERRY}; background:#fff; }

.bk-running { font-size:13.5px; line-height:1.5; color:${INK}; background:rgba(146,44,87,.06);
  border-radius:10px; padding:11px 13px; margin:0 0 18px; }
.bk-foot { display:flex; flex-wrap:wrap; gap:10px; justify-content:flex-end; align-items:center;
  margin-top:22px; padding-top:18px; border-top:1px solid ${BORDER}; }
/* Der zu zahlende Betrag links, die Knoepfe bleiben rechts aussen. Auf schmalen Breiten bricht
   die Zeile ueber die Knopfreihe, statt sie zusammenzuquetschen. */
/* Nicht MUTED: 13px auf hellem Grund brauchen 4.5:1, #9A9494 kommt nur auf rund 3:1. */
.bk-total { font-size:13px; color:#615C5C; margin:0 auto 0 0; }
.bk-total strong { color:${INK}; font-weight:700; }
.bk-btn { font-family:inherit; font-size:14px; font-weight:700; border-radius:10px; padding:11px 17px;
  cursor:pointer; border:1px solid ${BORDER}; background:#fff; color:${INK}; }
.bk-btn:hover:not(:disabled) { background:#FBFAFA; }
.bk-btn--go { background:${BERRY}; border-color:${BERRY}; color:#fff; }
.bk-btn--go:hover:not(:disabled) { background:#7E2549; }
.bk-btn:disabled { opacity:.45; cursor:default; }
.bk-btn:focus-visible, .bk-upsell:focus-within { outline:2px solid ${BERRY}; outline-offset:2px; }
${BILLING_CSS}
`;

/** Breite, in der die Card später wirklich steht — daraus ergibt sich der Maßstab der Vorschau. */
const CARD_WIDTH = 330;

/**
 * Zeigt beliebigen Inhalt in echter Größe gerechnet, aber auf die verfügbare Spalte skaliert.
 *
 * Die Höhe wird gemessen statt geraten: sie hängt am Inhalt (Titel über zwei Zeilen, Chips), und
 * ein fester Wert schnitte die Card irgendwann ab.
 */
const ScaledPreview: React.FC<{ cardWidth: number; children: React.ReactNode }> = ({ cardWidth, children }) => {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const measure = () => setSize({
      width: outer.current?.clientWidth ?? 0,
      height: inner.current?.offsetHeight ?? 0,
    });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    if (outer.current) ro.observe(outer.current);
    if (inner.current) ro.observe(inner.current);
    return () => ro.disconnect();
  }, [cardWidth]);

  // Nie über 1:1 hinaus: eine hochskalierte Card hätte riesige Schrift und wäre keine Vorschau
  // mehr, sondern eine Karikatur. Passt sie hinein, steht sie in Originalgröße mittig.
  const scale = size.width ? Math.min(1, size.width / cardWidth) : 0;
  return (
    <div
      ref={outer}
      className="bk-preview"
      style={{ height: size.height * scale, width: '100%', maxWidth: cardWidth }}
      aria-hidden
    >
      <div ref={inner} className="bk-preview-inner" style={{ width: cardWidth, transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
};

/** Die echte Card dieses Events in der Stufe, die gerade zur Wahl steht. */
export const TierPreview: React.FC<{ event: CyclingEvent; tier: PromotionTier | null }> = ({ event, tier }) => {
  const now = new Date().toISOString();
  const preview: CyclingEvent = {
    ...event,
    promotion: tier
      ? { tier, label: PROMOTION_DEFAULT_LABELS[tier], from: now, until: now, active: true }
      : undefined,
  };
  return (
    <ScaledPreview cardWidth={CARD_WIDTH}>
      <article className={`event-card ${promoCardClass(preview, true)}`}>
        <EventCardHero event={preview} favorited={false} preview />
        <EventCardBody event={preview} />
      </article>
    </ScaledPreview>
  );
};

/**
 * Ein Event hervorheben — der Checkout.
 *
 * **Ein Preis, ein Häkchen** (`docs/monetarisierung.md`, 11.09.2026). Verkauft wird der Rahmen,
 * immer bis zur Veranstaltung: „7 Tage" und „1 Monat" waren unsere Einheiten, nicht seine, und
 * niemand will, dass seine Anzeige drei Wochen vor dem Renntag aufhört. Die dunkle Card ist kein
 * zweites Angebot daneben, sondern ein Häkchen mit dem Aufpreis als Zahl: der Käufer bewertet
 * nicht neu, er entscheidet über eine Differenz. Bis dahin wählte der Dialog zweistufig aus drei
 * Stufen und drei Laufzeiten — neun Preise für null zahlende Kunden.
 *
 * Es wird nichts sofort bezahlt: die Anfrage geht an die Redaktion, die Bestätigung nennt Betrag,
 * Verwendungszweck und Konto. **Preise sind Nettopreise** (V159), die Steuer steht am Preis; der
 * zu zahlende Bruttobetrag steht genau einmal, in der Zeile am Knopf — sonst fände der Besteller
 * ihn erst auf dem Überweisungsträger. Verbindlich entscheidet der Server (Preis-Id, Stufe,
 * Laufzeit werden dort geprüft) — hier steht nur, was er anbietet.
 */
const BookingRequestModal: React.FC<Props> = ({
  show, organizerId, eventId, eventTitle, event, billingDefault, onClose, onBooked,
}) => {
  const [options, setOptions] = useState<BookingOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(false);
  const [note, setNote] = useState('');
  const [billing, setBilling] = useState(billingDefault);
  /**
   * Die Vorbelegung als Ref, nicht als Abhaengigkeit des Effekts unten: `billingDefault` ist ein
   * Objektliteral aus der Elternkomponente und damit bei jedem ihrer Renderdurchlaeufe ein neues
   * — in der Abhaengigkeitsliste wuerde der Effekt jedes Mal neu laufen und die Angebote erneut
   * holen, waehrend jemand das Formular ausfuellt.
   */
  const billingDefaultRef = useRef(billingDefault);
  billingDefaultRef.current = billingDefault;
  const [saving, setSaving] = useState(false);
  /**
   * Die abgeschickte Bestellung — solange sie steht, zeigt der Dialog die Bestätigung. Ein Kauf,
   * der mit dem Verschwinden des Fensters endet, fühlt sich an wie ein Klick ins Leere.
   */
  const [done, setDone] = useState<{
    reference: string; grossCents: number; vatPercent: number; taxTreatment?: TaxTreatment;
    bank?: BankAccount;
  } | null>(null);

  /** Läuft schon die dunkle Card, ist sie beim Verlängern die Voreinstellung — kein Rückfall. */
  const runningTier = event ? activePromotion(event)?.tier : undefined;

  useEffect(() => {
    if (!show) return;
    trackOrganizerFunnel('promotion_checkout_started', { offer: 'promotion', organizerId });
    setLoading(true);
    setDone(null);
    setDark(runningTier === PROMOTION_UPSELL_TIER);
    setNote('');
    setBilling(billingDefaultRef.current);
    bookingService.options(organizerId, eventId ? { eventId } : {})
      .then(setOptions)
      .catch(() => toast.error('Angebote konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [show, organizerId, eventId, runningTier]);

  /**
   * Läuft an diesem Event schon eine Hervorhebung? Läuft der Rahmen bis zur Veranstaltung, gibt
   * es an Laufzeit nichts mehr zu kaufen — dann bleibt nur die dunkle Card, und die ist dann
   * keine Zugabe mehr, sondern die Bestellung. Läuft schon die dunkle Card so lange, ist nichts
   * mehr zu kaufen; den Knopf dafür zeigt die Liste gar nicht erst (`promoActionLabel`).
   */
  const running = event ? activePromotion(event) : null;
  const runningCoversEvent = event ? promotionCoversEvent(event) : false;
  const onlyUpgrade = !!running && runningCoversEvent && running.tier === PROMOTION_BASE_TIER;

  const offer = useMemo(() => promotionOffer(options, PROMOTION_BASE_TIER, PROMOTION_UPSELL_TIER), [options]);
  const upsell = offer.upsell ?? null;
  const chosen: BookingOption | null = onlyUpgrade ? upsell : (dark && upsell ? upsell : offer.base ?? null);
  const chosenTier: PromotionTier | null = chosen?.promoTier ?? null;
  /** Der ganze Satz, warum gerade nichts geht. */
  const blocked = onlyUpgrade && !upsell
    ? 'Der Rahmen läuft schon bis zur Veranstaltung, und die dunkle Card ist gerade nicht buchbar.'
    : (!eventId ? 'Eine Hervorhebung buchst du direkt beim Event.' : offer.reason ?? null);

  if (!show) return null;

  const billingComplete = isBillingComplete(billing);
  /** Derselbe Steuerfall, den der Server gleich festschreibt (shared/src/tax.ts). */
  const treatment = taxTreatmentOf(billing.country, billing.vatId);

  const submit = async () => {
    if (!chosen || !billingComplete || blocked) return;
    setSaving(true);
    try {
      const created = await bookingService.create(organizerId, {
        // Der Server prüft ihn — er entscheidet, welcher Preis für diesen Veranstalter gilt.
        priceItemId: chosen.priceItemId,
        kind: 'PROMOTION',
        variant: chosen.variant,
        promoTier: chosen.promoTier,
        eventId,
        ...billingRequest(billing),
        note: note.trim() || undefined,
      });
      // `onBooked` gleich hier, damit die Liste dahinter „Anfrage läuft" zeigt, sobald die
      // Bestätigung geschlossen wird.
      setDone({
        reference: created.paymentReference,
        grossCents: created.grossCents,
        vatPercent: created.vatPercent,
        taxTreatment: created.taxTreatment,
        bank: created.bank,
      });
      trackOrganizerFunnel('promotion_requested', { offer: 'promotion', organizerId });
      onBooked();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Anfrage konnte nicht gesendet werden');
    } finally {
      setSaving(false);
    }
  };

  /** Nettopreis mit Steuerangabe daneben (V159); der zu zahlende Betrag steht am Knopf. */
  const priceLine = (option: BookingOption) => (
    <span className="bk-price-amount">
      {option.discountPercent > 0 && option.listNetCents != null && (
        <s className="bk-price-list" title="Preis ohne Premium">{formatEuro(option.listNetCents)}</s>
      )}
      {formatEuro(option.netCents)} <span>{vatNote(option.vatPercent)}</span>
      {option.discountPercent > 0 && (
        <span className="bk-discount">{option.discountPercent} % Premium-Rabatt</span>
      )}
    </span>
  );

  return (
    <div
      className="bk-backdrop"
      role="dialog"
      aria-modal="true"
      {...(done ? { 'aria-label': 'Buchung eingegangen' } : { 'aria-labelledby': 'bk-title' })}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
    >
      <style>{CSS}{BOOKING_DONE_CSS}</style>
      {done ? (
        <div className="bk-sheet bk-sheet--done">
          <BookingDone
            next="schalten wir die Hervorhebung frei"
            grossCents={done.grossCents}
            vatPercent={done.vatPercent}
            taxTreatment={done.taxTreatment}
            reference={done.reference}
            bank={done.bank}
            onClose={onClose}
          />
        </div>
      ) : (
      <div className="bk-sheet">
        <h2 id="bk-title" className="bk-title">
          {eventTitle ? `${running ? 'Hervorhebung' : 'Hervorheben'}: ${eventTitle}` : 'Event hervorheben'}
        </h2>
        <p className="bk-lead">
          Deine Card fällt in der Eventliste auf, bis zum Tag nach der Veranstaltung. Du bekommst
          die Zahlungsdaten per E-Mail; nach dem Geldeingang schalten wir frei.
        </p>

        {running && (
          <p className="bk-running">
            Aktuell läuft <strong>{PROMOTION_TIER_SALES[running.tier].name}</strong> bis{' '}
            {new Date(running.until).toLocaleDateString('de-DE')}.{' '}
            {onlyUpgrade
              ? 'Die dunkle Card ersetzt den Rahmen ab sofort.'
              : 'Dieselbe Stufe hängt sich hinten an, eine andere beginnt sofort und ersetzt die laufende.'}
          </p>
        )}

        {loading && <p style={{ color: MUTED, fontSize: 14 }}>Wird geladen…</p>}

        {!loading && (
          <>
            {event && (
              <div className="bk-stage">
                <TierPreview event={event} tier={blocked ? null : chosenTier} />
              </div>
            )}
            <p className="bk-benefit">
              {blocked
                ? 'So sieht deine Card heute in den Listen aus.'
                : PROMOTION_TIER_SALES[chosenTier ?? PROMOTION_BASE_TIER].benefit}
            </p>

            {blocked ? (
              <p className="bk-blocked" style={{ marginTop: 14 }}>{blocked}</p>
            ) : (
              <>
                {/* Der Grundpreis — oder, wenn der Rahmen schon läuft, die dunkle Card als
                    einzige Leistung. */}
                <div className="bk-price">
                  <div>
                    <span className="bk-price-name">
                      {onlyUpgrade
                        ? PROMOTION_TIER_SALES[PROMOTION_UPSELL_TIER].name
                        : PROMOTION_TIER_SALES[PROMOTION_BASE_TIER].name}
                    </span>
                    <span className="bk-price-sub">Bis zur Veranstaltung</span>
                  </div>
                  {priceLine(onlyUpgrade ? upsell! : offer.base!)}
                </div>

                {!onlyUpgrade && upsell && offer.upsellExtraCents != null && (
                  <label className={`bk-upsell${dark ? ' bk-upsell--on' : ''}`}>
                    <input type="checkbox" checked={dark} onChange={e => setDark(e.target.checked)} />
                    <span className="bk-upsell-body">
                      <span className="bk-upsell-name">
                        <span>{PROMOTION_TIER_SALES[PROMOTION_UPSELL_TIER].name} statt Rahmen</span>
                        <span className="bk-upsell-extra">+ {formatEuro(offer.upsellExtraCents)}</span>
                      </span>
                      <span className="bk-upsell-text">
                        {PROMOTION_TIER_SALES[PROMOTION_UPSELL_TIER].benefit}
                      </span>
                    </span>
                  </label>
                )}
              </>
            )}
          </>
        )}

        <label className="bk-field">
          <span>Anmerkung <span style={{ fontWeight: 400, color: MUTED }}>(optional)</span></span>
          <input
            className="bk-input"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Rückfrage, alles Weitere"
          />
        </label>

        {/* Rechnungsanschrift: Pflicht, weil die Rechnung erst nach dem Zahlungseingang
            geschrieben wird — danach antwortet niemand mehr. Siehe BillingFields. */}
        <p className="bk-legend" style={{ marginTop: 18 }}>Rechnung</p>
        <BillingFields value={billing} onChange={setBilling} narrow />

        <div className="bk-foot">
          {/* Der Bruttobetrag steht genau hier: das Angebot darüber rechnet netto, überwiesen
              wird brutto, und diese Zahl steht gleich darauf in der Bestätigung. */}
          {chosen && !blocked && (
            <p className="bk-total">
              Zu zahlen <strong>{formatEuro(payableCents(chosen, treatment))}</strong>{' '}
              {payableTaxLabel(treatment, chosen.vatPercent)}
            </p>
          )}
          <button className="bk-btn" onClick={onClose}>Abbrechen</button>
          <button className="bk-btn bk-btn--go" onClick={submit}
            disabled={!chosen || !!blocked || !billingComplete || saving}
            title={chosen && !billingComplete ? 'Bitte trag noch die Rechnungsanschrift ein.' : undefined}>
            {saving
              ? 'Wird gesendet…'
              : chosen
                ? `Kostenpflichtig anfragen · ${formatEuro(chosen.netCents)}`
                : 'Kostenpflichtig anfragen'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default BookingRequestModal;
